/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { unlockATicketForUser } from 'api/generalTicket'
import { startLocalSellingProcess } from 'api/simple_payment'
import { WrapperRow } from 'components/auditorium/auditorium.style'
import { ActionButton, TransparentButton } from 'components/button/button.styled'
import { EventCardListing } from 'components/eventCard/eventCardListing'
import { ButtonContainer } from 'components/form/form.style'
import { PlaceholderContainer } from 'components/placeholder'
import {
    Container,
    HeaderRow,
    PageTitle,
    PriceText,
    RemoveAllButton,
    SeatContainer,
    SeatLabel,
    SubSectionContainer,
    TicketInfo,
    TicketPrice,
} from 'components/ticket/ticket.style'
import { useCurrentAccount } from 'hooks/admin/useAuth'
import { usePayment } from 'hooks/payment/usePayment'
import { useToken } from 'hooks/token'
import { useSeatLocking } from 'hooks/user/useSelling'
import { useReservationTimer } from 'hooks/user/useTickets/useReservationTimer'
import { useTicketsLockedByUser } from 'hooks/user/useTickets/useTicketsLockedByUser'
import { Row } from 'primereact/row'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { EventDetails, SeatSellingModes, Ticket } from 'types/Event'
import { UserRole } from 'types/Organization'
import { FRONTEND_BASE_URL } from 'utils'
import { getSeatLabelForSeatId } from 'utils/seatLabel'

interface Props {
    currentEvent: EventDetails
}

export const LocalSellingCart = ({ currentEvent }: Props) => {
    const location = useLocation()
    const { sessionId } = useToken()
    const { currentAccount } = useCurrentAccount()
    const seatLocking = useSeatLocking(currentEvent.event_id, sessionId ?? '')

    const {
        data: ticketsLockedByUser,
        isLoading: ticketsLockedByUserIsLoading,
        error: ticketsLockedByUserError,
        refetch: ticketsLockedByUserRefetch,
    } = useTicketsLockedByUser(currentEvent.event_id, sessionId ?? '')
    const { formatted: reservationTime } = useReservationTimer(ticketsLockedByUser)

    const ticketIds = useMemo(() => {
        if (!ticketsLockedByUser) return []
        return ticketsLockedByUser.map((t: Ticket) => t.ticket_id)
    }, [ticketsLockedByUser])

    const seatIds = useMemo(() => {
        if (!ticketsLockedByUser) return []
        return ticketsLockedByUser.map((t: Ticket) => t.seat_id)
    }, [ticketsLockedByUser])

    const isComplimentary = useMemo(() => new URLSearchParams(location.search).get('isComplimentary') === 'true', [location.search])

    const isAdmin = currentAccount?.role === UserRole.orgAdmin || currentAccount?.role === UserRole.superAdmin

    const { handlePayment, selectedSeats, changeSelectedSeats, totalPrice, isLoadingPayment, paymentError } = usePayment({
        currentEvent,
        isComplimentary,
        isAdmin,
        getCustomerData: () => ({
            firstName: 'firstName',
            lastName: 'lastName',
            phoneNumber: 'phoneNumber',
            email: currentAccount?.email ?? 'org_admin',
        }),
        getInvoiceData: () => ({
            country: '',
            postCode: '',
            city: '',
            address: '',
            taxNumber: '',
            companyName: '',
            type: 'individual',
        }),
        startProcess: startLocalSellingProcess,
        urlPrefix: `${FRONTEND_BASE_URL}/admin/jegyek/letoltes/helyi`,
        seatIdsOverride: seatIds,
    })

    const removeSeat = async (seatId: string) => {
        await seatLocking?.unlockTicketHandler(seatId)
        window.location.reload()
    }

    const removeAllTickets = async () => {
        await unlockATicketForUser(currentEvent.event_id, sessionId ?? '', ticketsLockedByUser?.length)
        window.location.reload()
    }

    const isAnySeatSelected = ticketIds.length > 0 && ticketIds[0] !== ''

    const seatMap = useMemo(() => {
        const map = new Map<string, any>()
        currentEvent.location.auditorium.seats.forEach((seat) => {
            seat.seat_id && map.set(seat.seat_id, seat)
        })

        currentEvent.location.auditorium.gallery?.seats.forEach((seat) => {
            seat.seat_id && map.set(seat.seat_id, seat)
        })
        return map
    }, [currentEvent.location.auditorium.seats, currentEvent.location.auditorium.gallery?.seats])

    return (
        <Container>
            {currentEvent && ticketIds.length > 0 ? (
                <>
                    <PageTitle>Helyi jegyértékesítés kosár</PageTitle>
                    {currentEvent.event_id && <EventCardListing eventId={currentEvent.event_id} showPurchaseButton={false} />}

                    {currentEvent.seat_selling_mode === SeatSellingModes.SeatBased && (
                        <SubSectionContainer>
                            {ticketsLockedByUser?.map((ticket) => {
                                const seat = seatMap.get(ticket.seat_id)
                                if (!seat) {
                                    return <li key={ticket.seat_id}>Érvénytelen ülőhely: {ticket.seat_id}</li>
                                }

                                const seatLabel = getSeatLabelForSeatId(currentEvent, ticket.seat_id) ?? 'Ismeretlen hely'

                                const price = ticket.price ? `${ticket.price} Ft` : 'N/A'

                                return (
                                    <SeatContainer key={ticket.seat_id}>
                                        <SeatLabel>{seatLabel}</SeatLabel>
                                        <PriceText>{price}</PriceText>
                                        <ButtonContainer>
                                            <TransparentButton
                                                icon="pi pi-times"
                                                onClick={() => {
                                                    if (ticket) {
                                                        removeSeat(ticket.seat_id)
                                                    }
                                                }}
                                            />
                                        </ButtonContainer>
                                    </SeatContainer>
                                )
                            })}
                        </SubSectionContainer>
                    )}

                    {currentEvent.seat_selling_mode === SeatSellingModes.General && isAnySeatSelected && (
                        <SubSectionContainer>
                            <HeaderRow>
                                <TicketInfo>
                                    {reservationTime && (
                                        <Row>
                                            <strong style={{ color: '#D32F2F' }}>{reservationTime}</strong>
                                        </Row>
                                    )}
                                    {ticketIds.length} jegy kiválasztva
                                    {ticketIds.length > 0 &&
                                        (() => {
                                            const ticketPrices = ticketsLockedByUser?.map((ticket) => ticket.price)

                                            const uniquePrices = Array.from(new Set(ticketPrices))

                                            return uniquePrices.length === 1 ? `- ${uniquePrices[0]} Ft/jegy` : `- változó ár`
                                        })()}
                                </TicketInfo>

                                <RemoveAllButton onClick={removeAllTickets}>Összes eltávolítása</RemoveAllButton>
                            </HeaderRow>
                        </SubSectionContainer>
                    )}

                    <SubSectionContainer style={{ marginTop: '1rem' }}>
                        <WrapperRow>
                            <TicketPrice>Összesen:</TicketPrice>
                            <TicketPrice>{totalPrice} Ft</TicketPrice>
                        </WrapperRow>
                        <WrapperRow>
                            <ButtonContainer>
                                <ActionButton label={'Vásárlás véglegesítése'} onClick={handlePayment} disabled={isLoadingPayment} />
                            </ButtonContainer>
                        </WrapperRow>
                        {paymentError && <p style={{ color: 'red' }}>{paymentError}</p>}
                    </SubSectionContainer>
                </>
            ) : (
                <PlaceholderContainer>
                    {!currentEvent ? 'Ez az esemény sajnos nem elérhető. Kérjük térjen vissza a főoldalra.' : 'A kosár jelenleg üres.'}
                </PlaceholderContainer>
            )}
        </Container>
    )
}
